import { getReactiveLayer } from "./reactive-layer";
import { defaultComputedOptions } from "./reactive-options";
import {
	ComputedAtom,
	ComputedAtomOptions,
	CreateComputedAtom,
} from "./reactive-primitive-types";
import { observeRemoteAtoms } from "./reactive-remote-atoms";
import {
	getTrackingDerivation,
	isTrackingDerivation,
	setTrackingDerivation,
	untrackRemotes,
} from "./reactive-tracking";

/**
 * Creates a computed reactive atom that derives its value from other reactive sources.
 *
 * This function wraps the reactive layer's `createComputedAtom` primitive, adding
 * handling of reactive remotes.
 *
 * The computed value is cached and only re-evaluated if its dependencies change,
 * provided it's being observed by a reaction. Otherwise, it behaves like a
 * regular getter, re-computing on each call.
 *
 * @param name - A descriptive name for the computed atom, useful for debugging.
 * @param calculate - The function that computes the value of the atom. It will
 *   be re-run when its reactive dependencies change.
 * @param options - Optional configuration for the computed atom, such as
 *   custom equality checks (`equals`) or lifecycle listeners (`onBecomeObservedListener`,
 *   `onBecomeUnobservedListener`).
 * @returns A `ComputedAtom` instance.
 */
export const createComputedAtom: CreateComputedAtom = <T>(
	name: string,
	calculate: () => T,
	options?: ComputedAtomOptions
): ComputedAtom<T> => {
	const layerComputed = getReactiveLayer().createComputedAtom(
		name,
		() => {
			const previousDerivation = getTrackingDerivation();
			setTrackingDerivation(computedAtom);
			try {
				// The calculation is wrapped with `untrackRemotes` because we'd like
				// remotes to only depend on the computed atom, not on its dependencies
				return untrackRemotes(() => {
					return calculate();
				});
			} finally {
				setTrackingDerivation(previousDerivation);
			}
		},
		!options
			? defaultComputedOptions
			: {
					...defaultComputedOptions,
					...options,
			  }
	);

	const computedAtom: ComputedAtom<T> = {
		get() {
			// When this computed atom is accessed, notify any reactive remotes
			// that it is being observed. This allows remotes to establish their
			// own dependencies on this atom if they are tracking.
			observeRemoteAtoms(computedAtom, layerComputed, name);

			// If we are in a tracking context, calling `get()` will establish a
			// dependency in the underlying reactive layer. Otherwise, we wrap it
			// in `untrack` to get the value without creating a dependency.
			if (isTrackingDerivation()) {
				return layerComputed.get();
			}
			return getReactiveLayer().untrack(() => layerComputed.get());
		},
	};

	return computedAtom;
};
